<template>
    <div v-if="editMode">
        <div class="tooltip tooltip-right" data-tip="Culoare Fundal">
            <button
                class="btn btn-square btn-sm md:btn-md md:text-lg"
                @click.stop="openBar"
            >
                <font-awesome-icon icon="fa-solid fa-palette" />
            </button>
        </div>

        <Teleport to="body">
            <!-- Bottom bar -->
            <transition name="bottom-bar-slide">
                <div v-if="barOpen && !subPanel" class="bottom-edit-bar" @click.stop>
                    <div class="bottom-edit-bar__inner">
                        <!-- Solid color -->
                        <div class="bbar-item bbar-item--color" @click.stop="pickColor">
                            <span class="bbar-color-chip" :style="{ backgroundColor: localColor }"></span>
                            <input
                                ref="colorInput"
                                type="color"
                                v-model="localColor"
                                class="bbar-color-input"
                                @input="applyColor"
                                @click.stop
                            />
                            <span class="bbar-label">Culoare</span>
                        </div>

                        <!-- Gradient -->
                        <button class="bbar-item" :class="{ 'bbar-item--active': hasGradient }" @click.stop="openSubPanel('gradient')">
                            <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                            <span class="bbar-label">Gradient</span>
                        </button>

                        <!-- Remove gradient -->
                        <button v-if="hasGradient" class="bbar-item bbar-item--danger" @click.stop="removeGradient">
                            <font-awesome-icon icon="fa-solid fa-trash" />
                            <span class="bbar-label">Sterge</span>
                        </button>

                        <div class="bbar-divider"></div>

                        <button class="bbar-item" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                </div>
            </transition>

            <!-- Overlay for action sheet -->
            <transition name="action-sheet-fade">
                <div
                    v-if="subPanel"
                    class="action-sheet-overlay"
                    @click.stop="subPanel = null"
                ></div>
            </transition>

            <!-- Gradient action sheet -->
            <transition name="action-sheet-slide">
                <div v-if="subPanel === 'gradient'" class="action-sheet" @click.stop>
                    <div class="action-sheet__header">
                        <button class="action-sheet__close" @click.stop="subPanel = null">
                            <font-awesome-icon icon="fa-solid fa-arrow-left" />
                        </button>
                        <span class="action-sheet__title">Gradient fundal</span>
                        <button class="action-sheet__close" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <div class="bgc-gradient-body">
                        <!-- Presets -->
                        <div class="bgc-gradient-presets">
                            <button
                                v-for="(preset, idx) in gradientPresets"
                                :key="idx"
                                class="bgc-gradient-preset"
                                :style="{ background: preset.css }"
                                @click="applyPreset(preset)"
                            ></button>
                        </div>

                        <!-- Custom builder -->
                        <div class="bgc-gradient-custom">
                            <div class="bgc-gradient-preview" :style="{ background: customGradientCss }"></div>

                            <div class="bgc-gradient-controls">
                                <label class="bgc-ctrl-row">
                                    <span class="bgc-ctrl-label">Tip</span>
                                    <select v-model="gradType" class="bgc-ctrl-select" @change="applyCustom">
                                        <option value="linear">Linear</option>
                                        <option value="radial">Radial</option>
                                    </select>
                                </label>
                                <label v-if="gradType === 'linear'" class="bgc-ctrl-row">
                                    <span class="bgc-ctrl-label">Unghi</span>
                                    <input type="range" v-model.number="gradAngle" min="0" max="360" class="bgc-ctrl-range" @input="applyCustom" />
                                    <span class="bgc-ctrl-value">{{ gradAngle }}</span>
                                </label>
                                <label class="bgc-ctrl-row">
                                    <span class="bgc-ctrl-label">Culoare 1</span>
                                    <input type="color" v-model="gradColor1" class="bgc-ctrl-color" @input="applyCustom" />
                                </label>
                                <label class="bgc-ctrl-row">
                                    <span class="bgc-ctrl-label">Culoare 2</span>
                                    <input type="color" v-model="gradColor2" class="bgc-ctrl-color" @input="applyCustom" />
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    props: {
        value: String,
        config: Object,
        section: Object,
        sectionKey: String,
        propertyKey: String,
    },
    data() {
        return {
            localColor: this.value || '#ffffff',
            barOpen: false,
            subPanel: null,
            gradType: 'linear',
            gradAngle: 180,
            gradColor1: '#667eea',
            gradColor2: '#764ba2',
            gradientPresets: [
                { css: 'linear-gradient(135deg, #667eea, #764ba2)', type: 'linear', angle: 135, c1: '#667eea', c2: '#764ba2' },
                { css: 'linear-gradient(135deg, #f093fb, #f5576c)', type: 'linear', angle: 135, c1: '#f093fb', c2: '#f5576c' },
                { css: 'linear-gradient(135deg, #4facfe, #00f2fe)', type: 'linear', angle: 135, c1: '#4facfe', c2: '#00f2fe' },
                { css: 'linear-gradient(135deg, #43e97b, #38f9d7)', type: 'linear', angle: 135, c1: '#43e97b', c2: '#38f9d7' },
                { css: 'linear-gradient(135deg, #fa709a, #fee140)', type: 'linear', angle: 135, c1: '#fa709a', c2: '#fee140' },
                { css: 'linear-gradient(135deg, #a18cd1, #fbc2eb)', type: 'linear', angle: 135, c1: '#a18cd1', c2: '#fbc2eb' },
                { css: 'linear-gradient(180deg, #0c3483, #a2b6df)', type: 'linear', angle: 180, c1: '#0c3483', c2: '#a2b6df' },
                { css: 'linear-gradient(135deg, #ffecd2, #fcb69f)', type: 'linear', angle: 135, c1: '#ffecd2', c2: '#fcb69f' },
                { css: 'linear-gradient(180deg, #e0c3fc, #8ec5fc)', type: 'linear', angle: 180, c1: '#e0c3fc', c2: '#8ec5fc' },
                { css: 'linear-gradient(135deg, #ff9a9e, #fecfef)', type: 'linear', angle: 135, c1: '#ff9a9e', c2: '#fecfef' },
                { css: 'linear-gradient(180deg, #2b5876, #4e4376)', type: 'linear', angle: 180, c1: '#2b5876', c2: '#4e4376' },
                { css: 'linear-gradient(135deg, #d4fc79, #96e6a1)', type: 'linear', angle: 135, c1: '#d4fc79', c2: '#96e6a1' },
                { css: 'radial-gradient(circle, #fbc2eb, #a6c1ee)', type: 'radial', angle: 0, c1: '#fbc2eb', c2: '#a6c1ee' },
                { css: 'radial-gradient(circle, #ffecd2, #fcb69f)', type: 'radial', angle: 0, c1: '#ffecd2', c2: '#fcb69f' },
                { css: 'linear-gradient(135deg, #1a1a2e, #16213e)', type: 'linear', angle: 135, c1: '#1a1a2e', c2: '#16213e' },
                { css: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', type: 'linear', angle: 135, c1: '#f8f9fa', c2: '#e9ecef' },
            ],
        };
    },
    computed: {
        ...mapGetters(['isEditMode']),
        editMode() {
            return this.isEditMode;
        },
        hasGradient() {
            return !!this.section.backgroundGradient;
        },
        customGradientCss() {
            if (this.gradType === 'radial') {
                return `radial-gradient(circle, ${this.gradColor1}, ${this.gradColor2})`;
            }
            return `linear-gradient(${this.gradAngle}deg, ${this.gradColor1}, ${this.gradColor2})`;
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onDocClick);
    },
    methods: {
        openBar() {
            this.localColor = this.value || '#ffffff';
            this.barOpen = true;
        },
        closeAll() {
            this.barOpen = false;
            this.subPanel = null;
        },
        onDocClick() {
            if (this.barOpen && !this.subPanel) this.barOpen = false;
        },
        openSubPanel(panel) {
            this.subPanel = panel;
            // Sync custom builder with current gradient if exists
            if (panel === 'gradient' && this.section.backgroundGradient) {
                this.parseCurrentGradient();
            }
        },
        pickColor() {
            this.$refs.colorInput.click();
        },
        applyColor() {
            this.updateNestedValue(this.section, this.propertyKey, this.localColor);
            this.$emit('update-section', this.sectionKey, this.section);
        },
        applyPreset(preset) {
            this.gradType = preset.type;
            this.gradAngle = preset.angle;
            this.gradColor1 = preset.c1;
            this.gradColor2 = preset.c2;
            const updated = { ...this.section, backgroundGradient: preset.css };
            this.$emit('update-section', this.sectionKey, updated);
        },
        applyCustom() {
            const updated = { ...this.section, backgroundGradient: this.customGradientCss };
            this.$emit('update-section', this.sectionKey, updated);
        },
        removeGradient() {
            const updated = { ...this.section };
            delete updated.backgroundGradient;
            this.$emit('update-section', this.sectionKey, updated);
        },
        parseCurrentGradient() {
            const g = this.section.backgroundGradient || '';
            if (g.startsWith('radial')) {
                this.gradType = 'radial';
            } else {
                this.gradType = 'linear';
                const angleMatch = g.match(/(\d+)deg/);
                if (angleMatch) this.gradAngle = parseInt(angleMatch[1]);
            }
            const colorMatches = g.match(/#[0-9a-fA-F]{6}/g);
            if (colorMatches && colorMatches.length >= 2) {
                this.gradColor1 = colorMatches[0];
                this.gradColor2 = colorMatches[1];
            }
        },
        updateNestedValue(obj, path, value) {
            const keys = path.split('.');
            let o = obj;
            for (let i = 0; i < keys.length - 1; i++) {
                o = o[keys[i]];
            }
            o[keys[keys.length - 1]] = value;
        },
    },
};
</script>

<style>
/* Gradient action sheet body */
.bgc-gradient-body {
    padding: 12px 16px 20px;
}
.bgc-gradient-presets {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    margin-bottom: 14px;
}
.bgc-gradient-preset {
    aspect-ratio: 1.4;
    border-radius: 6px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: border-color 0.15s, transform 0.12s;
}
.bgc-gradient-preset:hover {
    border-color: #3b82f6;
    transform: scale(1.04);
}

/* Custom builder */
.bgc-gradient-custom {
    border-top: 1px solid #e2e8f0;
    padding-top: 14px;
}
.bgc-gradient-preview {
    width: 100%;
    height: 48px;
    border-radius: 10px;
    margin-bottom: 14px;
    border: 1px solid #e2e8f0;
}
.bgc-gradient-controls {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.bgc-ctrl-row {
    display: flex;
    align-items: center;
    gap: 10px;
}
.bgc-ctrl-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    min-width: 60px;
}
.bgc-ctrl-select {
    flex: 1;
    padding: 5px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    background: #fff;
    color: #334155;
}
.bgc-ctrl-range {
    flex: 1;
    accent-color: #3b82f6;
}
.bgc-ctrl-value {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    min-width: 28px;
    text-align: right;
}
.bgc-ctrl-color {
    width: 36px;
    height: 30px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 2px;
    cursor: pointer;
    background: none;
}
</style>
